import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import DomainSdk, { Domain } from "@smash-sdk/domain/ejs/10-2019";
import IamSdk, { Iam } from '@smash-sdk/iam/ejs/10-2019';
import { AsyncState } from '../../shared/models/async.interface';
import { RootState } from '../../store';
import { DeleteDomainInput } from './models/DeleteDomainInput.interface';
import { DomainStore } from './models/Domain.interface';
import domainService from './services/domain.service';
import { Team } from '../apiKey/models/Team.interface';

interface DeletedDomainState extends AsyncState {
    domain?: DomainStore | null;
}

interface CreatedDomainState extends AsyncState {
    domain?: DomainStore | null;
}

interface UpdatedDomainState extends AsyncState {
    domain?: DomainStore | null;
}
interface ListDomainState extends AsyncState {
    domains: DomainStore[];
}

interface DomainDetailsState extends AsyncState {
    details: {
        domain?: DomainSdk.GetDomainOutput['domain'] | null,
        team?: Team
    } | null; // FIX ME Fix SDK IAM and do not use local team interface
}

export interface DomainState {
    createdDomain: CreatedDomainState;
    updatedDomain: UpdatedDomainState;
    list: ListDomainState;
    domainDetails: DomainDetailsState;
    deletedDomain: DeletedDomainState;
}

export const createDomain = createAsyncThunk(
    'domain/createDomain',
    async (input: DomainSdk.CreateDomainInput, thunkAPI) => {
        try {
            return await domainService.createDomain(input);
        } catch (error: any) {
            return thunkAPI.rejectWithValue({ ...error });
        }
    }
);

export const updateTeamConfiguration = createAsyncThunk(
    'domain/updateTeamConfiguration',
    async (input: IamSdk.UpdateTeamInput, thunkApi) => {
        try {
            const iam2019 = new Iam();
            return await iam2019.updateTeam(input);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
);

export const listDomains = createAsyncThunk(
    'domain/listDomains',
    async (_, thunkApi) => {
        try {
            const { domains } = await domainService.listDomains();
            return { domains };
        } catch (error) {
            throw error;
        }
    }
)

export const getDomainDetails = createAsyncThunk(
    'domain/getDomainDetails',
    async (input: IamSdk.GetTeamInput, thunkApi) => {
        try {
            const domainSdk = new Domain();
            const iam2019 = new Iam();
            const { team } = await iam2019.getTeam(input) as unknown as { team: Team }; // FIX ME Fix SDK IAM and do not use local team interface
            if (team?.domain) {
                const { domain } = await domainSdk.getDomain({ domainId: team.domain });
                return { domain, team };
            }
            return { domain: null }
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const deleteDomain = createAsyncThunk(
    'domain/deleteDomain',
    async (input: DeleteDomainInput, thunkApi) => {
        try {
            return await domainService.deleteDomain(input);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const initialState: DomainState = {
    createdDomain: {
        domain: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
        error: undefined,
    },
    domainDetails: {
        details: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    list: {
        domains: [],
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    deletedDomain: {
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    updatedDomain: {
        isLoading: false,
        isSuccess: false,
        isError: false,
    }
}

export const domainSlice = createSlice({
    name: 'domain',
    initialState,
    reducers: {
        resetDomains: (state) => {
            state.list = {
                domains: state.list.domains,
                isLoading: false,
                isSuccess: false,
                isError: false,
            }
        },
        resetCreatedDomain: (state) => {
            state.createdDomain = {
                isLoading: false,
                isSuccess: false,
                isError: false,
                error: undefined,
            };
        },
        resetDeletedDomain: (state) => {
            state.deletedDomain = {
                isLoading: false,
                isSuccess: false,
                isError: false,
            };
        },
        resetDomainDetails: (state) => {
            state.domainDetails = {
                details: null,
                isLoading: false,
                isSuccess: false,
                isError: false,
            };
        },
        resetUpdateTeamConfiguration: (state) => {
            state.updatedDomain = {
                isLoading: false,
                isSuccess: false,
                isError: false,
            };
        }
    },
    extraReducers: (builder) => {
        builder
            // CEATE DOMAIN
            .addCase(createDomain.pending, (state) => {
                state.createdDomain.isLoading = true;
                state.createdDomain.isError = false;
                state.createdDomain.error = undefined;
            })
            .addCase(createDomain.fulfilled, (state, action) => {
                state.createdDomain.isLoading = false;
                state.createdDomain.isSuccess = true;
                state.createdDomain.domain = action.payload.domain;
            })
            .addCase(createDomain.rejected, (state, action) => {
                state.createdDomain.isLoading = false;
                state.createdDomain.isError = true;
                state.createdDomain.domain = null;
                state.createdDomain.error = action.error;
            })
            // DELETE KEY
            .addCase(deleteDomain.pending, (state) => {
                state.deletedDomain.isLoading = true;
                state.deletedDomain.isSuccess = false;
                state.deletedDomain.isError = false;
                state.deletedDomain.error = undefined;
            })
            .addCase(deleteDomain.fulfilled, (state) => {
                state.deletedDomain.isLoading = false;
                state.deletedDomain.isSuccess = true;
                state.deletedDomain.isError = false;
            })
            .addCase(deleteDomain.rejected, (state, action) => {
                state.deletedDomain.isLoading = false;
                state.deletedDomain.isSuccess = false;
                state.deletedDomain.isError = true;
                state.deletedDomain.error = action.error;
            })
            // LIST DOMAINS (Teams)
            .addCase(listDomains.pending, (state) => {
                state.list.isLoading = true;
                state.list.isError = false;
                state.list.error = undefined;
            })
            .addCase(listDomains.fulfilled, (state, action) => {
                state.list.isLoading = false;
                state.list.isSuccess = true;
                state.list.domains = action.payload.domains;
            })
            .addCase(listDomains.rejected, (state, action) => {
                state.list.isLoading = false;
                state.list.isError = true;
                state.list.domains = [];
                state.list.error = action.payload as SerializedError;
            })
            // GET DOMAIN DETAILS
            .addCase(getDomainDetails.pending, (state) => {
                state.domainDetails.isLoading = true;
                state.domainDetails.isError = false;
                state.domainDetails.error = undefined;
            })
            .addCase(getDomainDetails.fulfilled, (state, action) => {
                state.domainDetails.isLoading = false;
                state.domainDetails.isSuccess = true;
                state.domainDetails.details = { domain: action.payload.domain, team: action.payload.team };
            })
            .addCase(getDomainDetails.rejected, (state, action) => {
                state.domainDetails.isLoading = false;
                state.domainDetails.isError = true;
                state.domainDetails.details = null;
                state.domainDetails.error = action.error;
            })
            // UPDATE TEAM CONFIGURATION
            .addCase(updateTeamConfiguration.pending, (state) => {
                state.updatedDomain.isLoading = true;
                state.updatedDomain.isSuccess = false;
                state.updatedDomain.isError = false;
                state.updatedDomain.error = undefined;
            })
            .addCase(updateTeamConfiguration.fulfilled, (state, action) => {
                state.updatedDomain.isLoading = false;
                state.updatedDomain.isSuccess = true;
            })
            .addCase(updateTeamConfiguration.rejected, (state, action) => {
                state.updatedDomain.isLoading = false;
                state.updatedDomain.isError = true;
                state.updatedDomain.error = action.error;
            })
    }
});



export const { resetDomains, resetCreatedDomain, resetDomainDetails, resetDeletedDomain, resetUpdateTeamConfiguration } = domainSlice.actions;

export const domainState = (state: RootState) => state.domain;

export default domainSlice.reducer;